import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'lucide-react';

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  placeholder = "DD/MM/AAAA",
  label,
  required = false,
  disabled = false,
  error
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Converter data ISO para formato brasileiro (DD/MM/AAAA)
  const formatDateToBrazilian = (isoDate: string): string => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  // Converter formato brasileiro para ISO
  const formatDateToISO = (brazilianDate: string): string => {
    if (!brazilianDate) return '';
    
    // Remover caracteres não numéricos
    const cleanDate = brazilianDate.replace(/\D/g, '');
    
    // Se tem 8 dígitos, formatar como DDMMAAAA
    if (cleanDate.length === 8) {
      const day = cleanDate.substring(0, 2);
      const month = cleanDate.substring(2, 4);
      const year = cleanDate.substring(4, 8);
      
      // Validar se é uma data válida
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      if (date.getDate() == parseInt(day) && 
          date.getMonth() == parseInt(month) - 1 && 
          date.getFullYear() == parseInt(year)) {
        return `${year}-${month}-${day}`;
      }
    }
    
    return '';
  };

  // Validar data
  const validateDate = (dateStr: string): { isValid: boolean; message: string } => {
    if (!dateStr) {
      return { isValid: !required, message: required ? 'Data é obrigatória' : '' };
    }

    const cleanDate = dateStr.replace(/\D/g, '');
    
    if (cleanDate.length !== 8) {
      return { isValid: false, message: 'Data deve ter 8 dígitos (DDMMAAAA)' };
    }

    const day = parseInt(cleanDate.substring(0, 2));
    const month = parseInt(cleanDate.substring(2, 4));
    const year = parseInt(cleanDate.substring(4, 8));

    // Validar limites básicos
    if (day < 1 || day > 31) {
      return { isValid: false, message: 'Dia deve estar entre 01 e 31' };
    }
    
    if (month < 1 || month > 12) {
      return { isValid: false, message: 'Mês deve estar entre 01 e 12' };
    }
    
    if (year < 1900 || year > 2100) {
      return { isValid: false, message: 'Ano deve estar entre 1900 e 2100' };
    }

    // Validar se é uma data real
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
      return { isValid: false, message: 'Data inválida' };
    }

    // Validar se não é data futura (para nascimento)
    const today = new Date();
    if (date > today) {
      return { isValid: false, message: 'Data não pode ser futura' };
    }

    // Validar se não é muito antiga (mais de 120 anos)
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 120);
    if (date < minDate) {
      return { isValid: false, message: 'Data muito antiga' };
    }

    return { isValid: true, message: '' };
  };

  // Formatar input enquanto digita
  const formatInput = (input: string): string => {
    const cleanInput = input.replace(/\D/g, '');
    
    if (cleanInput.length <= 2) {
      return cleanInput;
    } else if (cleanInput.length <= 4) {
      return `${cleanInput.substring(0, 2)}/${cleanInput.substring(2)}`;
    } else {
      return `${cleanInput.substring(0, 2)}/${cleanInput.substring(2, 4)}/${cleanInput.substring(4, 8)}`;
    }
  };

  // Inicializar valor
  useEffect(() => {
    if (value) {
      setDisplayValue(formatDateToBrazilian(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatInput(inputValue);
    
    setDisplayValue(formattedValue);
    
    // Validar se tem 8 dígitos
    const cleanValue = formattedValue.replace(/\D/g, '');
    if (cleanValue.length === 8) {
      const isoDate = formatDateToISO(formattedValue);
      const validation = validateDate(formattedValue);
      
      setIsValid(validation.isValid);
      setErrorMessage(validation.message);
      
      if (validation.isValid && isoDate) {
        onChange(isoDate);
      } else if (validation.isValid && !isoDate) {
        // Se formato está correto mas não conseguiu converter, tentar novamente
        setTimeout(() => {
          const retryISO = formatDateToISO(formattedValue);
          if (retryISO) {
            onChange(retryISO);
          }
        }, 100);
      }
    } else {
      setIsValid(true);
      setErrorMessage('');
      onChange('');
    }
  };

  const handleCalendarClick = () => {
    // Abrir seletor de data nativo usando o input escondido
    if (dateInputRef.current) {
      dateInputRef.current.showPicker?.();
    }
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;
    if (selectedDate) {
      onChange(selectedDate);
      setDisplayValue(formatDateToBrazilian(selectedDate));
      setIsValid(true);
      setErrorMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Permitir apenas números, backspace, delete, tab, enter, escape
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape'];
    const isNumber = e.key >= '0' && e.key <= '9';
    
    if (!allowedKeys.includes(e.key) && !isNumber) {
      e.preventDefault();
    }
  };

  const inputId = `date-input-${Math.random().toString(36).substr(2, 9)}`;
  const dateInputId = `date-input-native-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label htmlFor={inputId} style={{
          display: 'block',
          marginBottom: '5px',
          fontWeight: 'bold',
          color: '#374151'
        }}>
          {label}
          {required && <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <input
          id={inputId}
          name="date"
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          autoComplete="off"
          style={{
            width: '100%',
            padding: '12px 40px 12px 12px',
            border: `2px solid ${error || !isValid ? '#dc2626' : '#d1d5db'}`,
            borderRadius: '6px',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: disabled ? '#f9fafb' : 'white',
            transition: 'border-color 0.2s',
            fontFamily: 'monospace'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = error || !isValid ? '#dc2626' : '#3b82f6';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error || !isValid ? '#dc2626' : '#d1d5db';
          }}
        />
        
        <button
          type="button"
          onClick={handleCalendarClick}
          disabled={disabled}
          aria-label="Abrir calendário"
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: '4px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Abrir calendário"
        >
          <Calendar size={20} />
        </button>
        
        {/* Input escondido para o date picker nativo */}
        <input
          id={dateInputId}
          name="date-native"
          ref={dateInputRef}
          type="date"
          value={value || ''}
          onChange={handleDateInputChange}
          disabled={disabled}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: 'none'
          }}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      
      {(error || !isValid) && (
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>⚠️</span>
          {error || errorMessage}
        </div>
      )}
      
      <div style={{
        marginTop: '4px',
        fontSize: '11px',
        color: '#6b7280'
      }}>
        💡 Digite a data no formato DD/MM/AAAA ou clique no calendário
      </div>
    </div>
  );
};

export default DateInput;
